import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageHeader from '../../components/PageHeader';
import {
  useCrawlQueueEntriesQuery,
  useCrawlQueueStatsQuery,
  useCortexConfigQuery,
  useUpdateCrawlQueueConfigMutation,
  useLockCrawlQueueEntriesMutation,
  useUnlockCrawlQueueEntriesMutation,
  useDeleteCrawlQueueEntriesMutation,
} from '../../gql/generated/graphql';
import { useSnackbar } from '../../providers/SnackbarProvider';

type FilterTab = 'all' | 'pending' | 'locked' | 'started' | 'errored';

const CrawlQueuePage = () => {
  const { showSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [configDraft, setConfigDraft] = useState<{
    enabled?: boolean;
    batchSize?: number;
    intervalSeconds?: number;
  }>({});
  const [configDirty, setConfigDirty] = useState(false);

  const filterVars = (() => {
    switch (activeTab) {
      case 'pending':
        return { crawlStarted: false, locked: false, hasError: false };
      case 'locked':
        return { locked: true };
      case 'started':
        return { crawlStarted: true };
      case 'errored':
        return { hasError: true };
      default:
        return {};
    }
  })();

  const {
    data: entriesData,
    loading: entriesLoading,
    refetch: refetchEntries,
  } = useCrawlQueueEntriesQuery({
    variables: { ...filterVars, limit: 500, offset: 0 },
    fetchPolicy: 'network-only',
  });

  const { data: statsData, refetch: refetchStats } = useCrawlQueueStatsQuery({ fetchPolicy: 'network-only' });

  const { data: cortexData } = useCortexConfigQuery({
    onCompleted: data => {
      if (data?.cortexConfig) {
        setConfigDraft({
          enabled: data.cortexConfig.crawlQueueEnabled ?? true,
          batchSize: data.cortexConfig.crawlQueueBatchSize ?? 5,
          intervalSeconds: data.cortexConfig.crawlQueueIntervalSeconds ?? 120,
        });
      }
    },
  });

  const [updateConfig] = useUpdateCrawlQueueConfigMutation();
  const [lockEntries] = useLockCrawlQueueEntriesMutation();
  const [unlockEntries] = useUnlockCrawlQueueEntriesMutation();
  const [deleteEntries] = useDeleteCrawlQueueEntriesMutation();

  const handleSaveConfig = async () => {
    try {
      await updateConfig({ variables: configDraft });
      showSnackbar('Crawl queue config updated', 'success');
      setConfigDirty(false);
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleLock = async () => {
    try {
      const result = await lockEntries({ variables: { ids: selectedIds } });
      const count = result.data?.lockCrawlQueueEntries?.updatedCount ?? 0;
      showSnackbar(`Locked ${count} entries`, 'success');
      setSelectedIds([]);
      refetchEntries();
      refetchStats();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleUnlock = async () => {
    try {
      const result = await unlockEntries({ variables: { ids: selectedIds } });
      const count = result.data?.unlockCrawlQueueEntries?.updatedCount ?? 0;
      showSnackbar(`Unlocked ${count} entries`, 'success');
      setSelectedIds([]);
      refetchEntries();
      refetchStats();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} entries? This cannot be undone.`)) return;
    try {
      const result = await deleteEntries({ variables: { ids: selectedIds } });
      const count = result.data?.deleteCrawlQueueEntries?.deletedCount ?? 0;
      showSnackbar(`Deleted ${count} entries`, 'success');
      setSelectedIds([]);
      refetchEntries();
      refetchStats();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleRefresh = () => {
    refetchEntries();
    refetchStats();
  };

  const stats = statsData?.crawlQueueStats;

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 200 },
    { field: 'companyName', headerName: 'Company', flex: 1, minWidth: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      valueGetter: (_value: any, row: any) => {
        if (row.errorMessage) return 'Error';
        if (row.crawlStarted) return 'Started';
        if (row.locked) return 'Locked';
        return 'Pending';
      },
      renderCell: params => {
        const colorMap: Record<string, 'error' | 'success' | 'warning' | 'default'> = {
          Error: 'error',
          Started: 'success',
          Locked: 'warning',
          Pending: 'default',
        };
        return <Chip label={params.value} color={colorMap[params.value as string] ?? 'default'} size="small" />;
      },
    },
    { field: 'triggeredBy', headerName: 'Triggered By', width: 140 },
    {
      field: 'datetimeCreated',
      headerName: 'Created',
      width: 170,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'errorMessage',
      headerName: 'Error',
      flex: 1,
      minWidth: 200,
      renderCell: params =>
        params.value ? (
          <Tooltip title={params.value}>
            <Typography variant="body2" color="error" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        ) : null,
    },
  ];

  const rows = entriesData?.crawlQueueEntries ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Crawl Queue" />

      {/* Config Bar */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={configDraft.enabled ?? true}
                onChange={e => {
                  setConfigDraft(prev => ({ ...prev, enabled: e.target.checked }));
                  setConfigDirty(true);
                }}
              />
            }
            label="Queue Enabled"
          />
          <TextField
            label="Batch Size"
            type="number"
            size="small"
            value={configDraft.batchSize ?? ''}
            onChange={e => {
              setConfigDraft(prev => ({ ...prev, batchSize: parseInt(e.target.value) || 0 }));
              setConfigDirty(true);
            }}
            sx={{ width: 120 }}
          />
          <TextField
            label="Interval (s)"
            type="number"
            size="small"
            value={configDraft.intervalSeconds ?? ''}
            onChange={e => {
              setConfigDraft(prev => ({ ...prev, intervalSeconds: parseInt(e.target.value) || 0 }));
              setConfigDirty(true);
            }}
            sx={{ width: 120 }}
          />
          <Button variant="contained" startIcon={<SaveIcon />} disabled={!configDirty} onClick={handleSaveConfig}>
            Save
          </Button>
        </Stack>
      </Box>

      {/* Stats Chips */}
      {stats && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label={`Total: ${stats.total}`} />
          <Chip label={`Pending: ${stats.pending}`} color="default" />
          <Chip label={`Locked: ${stats.locked}`} color="warning" />
          <Chip label={`Started: ${stats.started}`} color="success" />
          <Chip label={`Errored: ${stats.errored}`} color="error" />
        </Stack>
      )}

      {/* Filter Tabs + Refresh */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => {
            setActiveTab(v);
            setSelectedIds([]);
          }}
        >
          <Tab label="All" value="all" />
          <Tab label="Pending" value="pending" />
          <Tab label="Locked" value="locked" />
          <Tab label="Started" value="started" />
          <Tab label="Errored" value="errored" />
        </Tabs>
        <IconButton onClick={handleRefresh}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Batch Toolbar */}
      {selectedIds.length > 0 && (
        <Toolbar variant="dense" sx={{ mb: 1, bgcolor: 'action.selected', borderRadius: 1, gap: 1 }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {selectedIds.length} selected
          </Typography>
          <Button size="small" startIcon={<LockIcon />} onClick={handleLock}>
            Lock
          </Button>
          <Button size="small" startIcon={<LockOpenIcon />} onClick={handleUnlock}>
            Unlock
          </Button>
          <Button size="small" startIcon={<DeleteOutlineIcon />} color="error" onClick={handleDelete}>
            Delete
          </Button>
        </Toolbar>
      )}

      {/* DataGrid */}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={entriesLoading}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={(ids: any) => setSelectedIds(ids as number[])}
        getRowId={row => row.id}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{ height: 600 }}
      />
    </Box>
  );
};

export default CrawlQueuePage;
